import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  GoogleAuthProvider,
  EmailAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
  confirmPasswordReset,
  reauthenticateWithCredential,
  updatePassword,
  signOut,
  updateProfile,
  reload,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  type User,
} from 'firebase/auth';
import { translateFirebaseErrorObject } from '../utils/firebaseErrors';
import { resolveMessagingSenderId } from './firebase-sender-id';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: resolveMessagingSenderId(
    import.meta.env.VITE_FIREBASE_APP_ID,
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  ),
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const app = getApps().length ? getApps()[0]! : initializeApp(firebaseConfig);
export const auth = getAuth(app);
auth.useDeviceLanguage();

if (import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true') {
  try {
    connectAuthEmulator(auth, 'http://localhost:9099');
  } catch {
    /* emulator already connected */
  }
}

function wrapFirebaseError(error: unknown): Error {
  const translated = new Error(translateFirebaseErrorObject(error));
  if (error && typeof error === 'object' && 'name' in error) {
    translated.name = String((error as { name: string }).name);
  }
  return translated;
}

export async function signInGoogle(): Promise<User> {
  try {
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');
    provider.setCustomParameters({ prompt: 'select_account' });
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error) {
    throw wrapFirebaseError(error);
  }
}

export async function signInEmail(
  email: string,
  password: string,
  rememberMe = true,
): Promise<User> {
  try {
    await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  } catch (error) {
    throw wrapFirebaseError(error);
  }
}

export async function signUpEmail(
  email: string,
  password: string,
  displayName?: string,
): Promise<User> {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) {
      await updateProfile(result.user, { displayName });
    }
    await sendEmailVerification(result.user, {
      url: `${window.location.origin}/email-action`,
      handleCodeInApp: true,
    });
    return result.user;
  } catch (error) {
    throw wrapFirebaseError(error);
  }
}

export async function doSignOut(): Promise<void> {
  try {
    await signOut(auth);
  } catch (error) {
    throw wrapFirebaseError(error);
  }
}

export async function sendPasswordReset(email: string): Promise<void> {
  try {
    await sendPasswordResetEmail(auth, email, {
      url: `${window.location.origin}/email-action`,
      handleCodeInApp: true,
    });
  } catch (error) {
    throw wrapFirebaseError(error);
  }
}

export async function resendVerification(user: User): Promise<void> {
  try {
    await sendEmailVerification(user, {
      url: `${window.location.origin}/email-action`,
      handleCodeInApp: true,
    });
  } catch (error) {
    throw wrapFirebaseError(error);
  }
}

export async function refreshUser(user: User): Promise<User> {
  try {
    await reload(user);
    return auth.currentUser ?? user;
  } catch (error) {
    throw wrapFirebaseError(error);
  }
}

export async function verifyResetCode(code: string): Promise<string> {
  try {
    return await verifyPasswordResetCode(auth, code);
  } catch (error) {
    throw wrapFirebaseError(error);
  }
}

export async function resetPassword(code: string, newPassword: string): Promise<void> {
  try {
    await confirmPasswordReset(auth, code, newPassword);
  } catch (error) {
    throw wrapFirebaseError(error);
  }
}

export function hasPasswordProvider(user: User | null | undefined): boolean {
  return !!user?.providerData.some((provider) => provider.providerId === 'password');
}

export function hasGoogleProvider(user: User | null | undefined): boolean {
  return !!user?.providerData.some((provider) => provider.providerId === 'google.com');
}

export async function updateAuthPhoto(user: User, photoURL: string): Promise<User> {
  try {
    await updateProfile(user, { photoURL });
    return auth.currentUser ?? user;
  } catch (error) {
    throw wrapFirebaseError(error);
  }
}

/** Troca a senha do usuário logado (exige reautenticação com a senha atual). */
export async function changePassword(
  user: User,
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  try {
    if (!user.email) {
      throw new Error('Não foi possível alterar a senha: conta sem e-mail.');
    }
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  } catch (error) {
    throw wrapFirebaseError(error);
  }
}
